import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
} from '@angular/core';
import { RouterLink } from '@angular/router';

import { AuthService } from '@personal-area/global/authorization/services';
import { LoadingState } from '@personal-area/global/state';
import { ButtonComponent } from '@personal-area/global/ui'
import { LetDirective } from '@ngrx/component';
import { TranslateModule } from '@ngx-translate/core';
import {
  combineLatestWith,
  map,
  takeWhile,
  tap,
  timeout,
} from 'rxjs';

import { IconSpriteModule } from '@personal-area/icon-sprite';
import { CircleProgressComponent } from '@personal-area/global/ui';

import {
  BasicVerificationStatus,
  IDVerificationStatus,
} from '../../models/verification-profile-response';
import { VerificationProfileApiService } from '../../services/verification-profile-api.service';
import { CardVerificationFacade } from '../../state/facades/card';
import { SummaryVerificationFacade } from '../../state/facades/summary';
import { CardState } from '../../state/reducers/card';
import { SummaryState } from '../../state/reducers/summary';
import { BannerFrame } from '../../constants/verification-banner-frame';

type BannerState = { frame: BannerFrame } | { frame: BannerFrame; days: number };

const MAX_YEAR = 9999;
const MS_IN_DAY = 1000 * 60 * 60 * 24;
const TIMEOUT = 30_000;

function toBannerState([summaryState, cardState]: [
  SummaryState,
  CardState,
]): BannerState {
  if (summaryState.loadingState === LoadingState.failed) {
    return cardState.loadingState === LoadingState.loaded && cardState.card
      ? { frame: 'card' }
      : { frame: 'hidden' };
  }
  if (
    (summaryState.basicStatus === BasicVerificationStatus.none ||
      summaryState.basicStatus === BasicVerificationStatus.basicFormPassed) &&
    [IDVerificationStatus.none, IDVerificationStatus.started].includes(summaryState.idStatus)
  ) {
    return { frame: 'lets-verified' };
  }
  if (
    summaryState.basicStatus === BasicVerificationStatus.trial &&
    [IDVerificationStatus.none, IDVerificationStatus.started].includes(
      summaryState.idStatus,
    )
  ) {
    const trialDaysRemain =
      Math.abs(summaryState.basicTrialExpirationDate.getTime() - Date.now()) /
      MS_IN_DAY;

    if (summaryState.basicTrialExpirationDate.getUTCFullYear() >= MAX_YEAR) {
      return { frame: 'hidden' };
    }
    if (trialDaysRemain > 1) {
      return { frame: 'trial-remaining-days', days: Math.ceil(trialDaysRemain) };
    }
    return { frame: 'trial-end-today' };
  }
  if (
    summaryState.basicStatus === BasicVerificationStatus.expired &&
    [IDVerificationStatus.none, IDVerificationStatus.started].includes(
      summaryState.idStatus,
    )
  ) {
    return { frame: 'trial-expired' };
  }
  if (summaryState.idStatus === IDVerificationStatus.pending) {
    return summaryState.basicStatus === BasicVerificationStatus.none
      ? { frame: 'full-pending-express-possible' }
      : { frame: 'full-pending' };
  }
  if (summaryState.idStatus === IDVerificationStatus.rejected) {
    return summaryState.basicStatus === BasicVerificationStatus.none
      ? { frame: 'full-rejected-express-posible' }
      : { frame: 'full-rejected' };
  }
  if (summaryState.idStatus === IDVerificationStatus.banned) {
    return { frame: 'banned' };
  }
  if (cardState.loadingState === LoadingState.loaded && cardState.card) {
    return { frame: 'card' };
  }
  if (summaryState.idStatus === IDVerificationStatus.accepted) {
    return new Date() > summaryState.idCompleteExpirationDate
      ? { frame: 'hidden' }
      : { frame: 'completed' };
  }
  return { frame: 'hidden' };
}

@Component({
  selector: 'global-profile-verification-banner',
  templateUrl: './verification-banner-profile.component.html',
  styleUrls: ['./verification-banner-profile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    CircleProgressComponent,
    RouterLink,
    IconSpriteModule,
    ButtonComponent,
    LetDirective,
  ],
})
export class VerificationBannerProfileComponent {
  public isLoading = true;
  public isHidden = false;
  public state$ = this._summaryFacade.summary$.pipe(
    combineLatestWith(this._cardFacade.card$),
    map(toBannerState),
    timeout({ first: TIMEOUT }),
    tap({
      next: ({ frame }) => {
        this.isHidden = frame === 'hidden';
        this.isLoading = false;
        this._cdRef.detectChanges();
      },
      error: () => {
        this.isHidden = true;
        this._cdRef.detectChanges();
      },
    }),
    takeWhile(({ frame }) => frame !== 'hidden'),
  );

  constructor(
    private _cardFacade: CardVerificationFacade,
    private _summaryFacade: SummaryVerificationFacade,
    private _authService: AuthService,
    private _verificationProfileApiService: VerificationProfileApiService,
    private _cdRef: ChangeDetectorRef,
  ) {}

  public close(): void {
    this._verificationProfileApiService.closeBanner(this._authService.userId)
      .subscribe(() => this._summaryFacade.expireBanner());
  }
}
